import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, IconButton, Button,
  CircularProgress, Divider, Select, MenuItem, FormControl, Tooltip,
} from '@mui/material';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import ViewModuleIcon     from '@mui/icons-material/ViewModule';
import ViewColumnIcon     from '@mui/icons-material/ViewColumn';
import MapIcon            from '@mui/icons-material/Map';
import TableRowsIcon      from '@mui/icons-material/TableRows';
import HubIcon            from '@mui/icons-material/Hub';
import SettingsIcon       from '@mui/icons-material/Settings';
import DeleteOutlineIcon  from '@mui/icons-material/DeleteOutline';
import LagbasLogo from '../../../../components/LagbasLogo';
import { db } from '../../../../firebase/config';
import {
  collection, doc, getDoc, getDocs, query, orderBy,
  onSnapshot, updateDoc,
} from 'firebase/firestore';
import ReactFlow, {
  Background, Controls, MiniMap,
  useNodesState, useEdgesState, addEdge, applyEdgeChanges,
  Handle, Position, Panel, MarkerType, NodeResizer,
  updateEdge as rfUpdateEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Toaster } from 'sonner';
import { MAPPN_ACCENT, STATUS_OPTIONS, IMPL_OPTIONS, uid } from '../constants';
import ComponentDetail from '../components/ComponentDetail';

// ── Edge type config ──────────────────────────────────────────────────────────

const EDGE_KINDS = [
  { key: 'simple',  label: '→',      color: MAPPN_ACCENT, bidir: false },
  { key: 'bidir',   label: '↔',      color: '#90CAF9',    bidir: true  },
  { key: 'api',     label: 'API',    color: '#42A5F5',    bidir: false },
  { key: 'native',  label: 'Nativ',  color: '#66BB6A',    bidir: false },
  { key: 'hybrid',  label: 'Hybrid', color: '#FFA726',    bidir: false },
];

const PATH_STYLES = [
  { key: 'smoothstep', label: 'Rundad'  },
  { key: 'step',       label: 'Kantig'  },
  { key: 'straight',   label: 'Rak'     },
  { key: 'default',    label: 'Bezier'  },
];

function buildEdgeProps(kind, pathStyle, showLabels = true) {
  const ek   = EDGE_KINDS.find(e => e.key === kind) || EDGE_KINDS[0];
  const col  = ek.color;
  const base = {
    type:      pathStyle || 'smoothstep',
    style:     { stroke: col + 'cc', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: col + 'cc', width: 16, height: 16 },
    data:      { edgeKind: ek.key },
    label:     undefined,
    labelStyle: undefined,
    labelBgStyle: undefined,
    labelBgPadding: undefined,
  };
  if (ek.bidir) base.markerStart = { type: MarkerType.ArrowClosed, color: col + 'cc', width: 16, height: 16 };
  if (showLabels && ek.key !== 'simple' && ek.key !== 'bidir') {
    base.label          = ek.label;
    base.labelStyle     = { fill: col, fontSize: '0.6rem', fontWeight: 700 };
    base.labelBgStyle   = { fill: 'rgba(13,17,23,0.85)' };
    base.labelBgPadding = [4, 2];
  }
  return base;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function autoPos(index) {
  const cols = 4;
  return { x: (index % cols) * 280 + 40, y: Math.floor(index / cols) * 200 + 40 };
}

// ── Frame (group) node ────────────────────────────────────────────────────────

function FrameNode({ data, selected }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel]     = useState(data.label || '');
  const inputRef = useRef(null);

  useEffect(() => { setLabel(data.label || ''); }, [data.label]);

  function commit() {
    setEditing(false);
    const trimmed = label.trim();
    if (trimmed !== (data.label || '')) data.onRename?.(trimmed);
  }

  const fc = data.color || null;
  const borderColor = selected
    ? (fc ? fc + 'ee' : MAPPN_ACCENT + 'cc')
    : (fc ? fc + '88' : 'rgba(255,255,255,0.22)');
  const bgColor = fc
    ? (selected ? fc + '2a' : fc + '18')
    : (selected ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.025)');

  return (
    <Box
      sx={{
        width: '100%', height: '100%',
        border: `2px solid ${borderColor}`,
        borderRadius: 2,
        bgcolor: bgColor,
        position: 'relative',
        transition: 'border-color 0.15s, background 0.15s',
        cursor: selected ? 'default' : 'pointer',
      }}
    >
      <NodeResizer
        minWidth={100}
        minHeight={60}
        isVisible={selected}
        onResizeEnd={(_, params) => data.onResize?.(params)}
        color={fc || MAPPN_ACCENT}
        handleStyle={{ width: 12, height: 12, borderRadius: 3 }}
      />

      {/* Label row */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        position: 'absolute', top: 6, left: 8,
        right: selected ? 38 : 8,
        overflow: 'hidden',
      }}>
        {editing ? (
          <input
            ref={inputRef}
            value={label}
            onChange={e => setLabel(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') { setEditing(false); setLabel(data.label || ''); }
            }}
            style={{
              background: 'transparent', border: 'none',
              borderBottom: '1px solid ' + (fc || MAPPN_ACCENT),
              color: 'white', fontSize: '0.78rem', fontWeight: 700,
              outline: 'none', flex: 1, minWidth: 0,
            }}
          />
        ) : (
          <Typography
            onDoubleClick={() => { setEditing(true); setTimeout(() => inputRef.current?.focus(), 10); }}
            sx={{
              fontSize: '0.78rem', fontWeight: 700, userSelect: 'none',
              color: label
                ? (fc ? fc + 'ee' : 'rgba(255,255,255,0.55)')
                : 'rgba(255,255,255,0.18)',
              cursor: 'text',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {label || 'Dubbelklicka för att namnge'}
          </Typography>
        )}
      </Box>

      {/* Controls — visible when selected */}
      {selected && (
        <Box sx={{ position: 'absolute', top: 4, right: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1, mb: 0.25 }}>Färg</Typography>
          <input
            type="color"
            value={data.color || '#7c3aed'}
            onChange={e => data.onColorChange?.(e.target.value)}
            onClick={e => e.stopPropagation()}
            style={{
              width: 26, height: 26,
              border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4,
              cursor: 'pointer', padding: 0,
              background: 'transparent',
            }}
          />
          <Box
            onClick={e => { e.stopPropagation(); data.onDelete?.(); }}
            sx={{
              width: 22, height: 22, borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#ef5350',
              border: '1px solid rgba(239,83,80,0.3)',
              '&:hover': { bgcolor: 'rgba(239,83,80,0.15)' },
              fontSize: '0.75rem', fontWeight: 700,
            }}
            title="Ta bort grupp"
          >✕</Box>
        </Box>
      )}
    </Box>
  );
}

// ── Component node (no hover expansion) ──────────────────────────────────────

const HANDLE_STYLE = {
  width: 10, height: 10, borderRadius: '50%',
  background: MAPPN_ACCENT, border: '2px solid #0d1117',
};

function ComponentNode({ data }) {
  const [hovered, setHovered] = useState(false);
  const { comp, categories, colorMode, priority = 1 } = data;

  const status = STATUS_OPTIONS.find(s => s.key === comp.status);
  const cat    = categories.find(c => c.id === comp.categoryId);
  const impl   = IMPL_OPTIONS.find(o => o.key === comp.implementation);

  const dimmed = priority >= 2;
  let accentColor = status?.color || '#78909C';
  if (colorMode === 'category'       && cat)  accentColor = cat.color;
  if (colorMode === 'implementation' && impl) accentColor = impl.color;

  return (
    <>
      <Handle type="target" position={Position.Left}  style={HANDLE_STYLE} />
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          bgcolor: '#161b22',
          border: `1.5px solid ${hovered ? accentColor + '99' : accentColor + '44'}`,
          borderLeft: `3px solid ${accentColor}`,
          opacity: dimmed ? 0.3 : 1,
          filter:  dimmed ? 'grayscale(0.6)' : 'none',
          borderRadius: '8px',
          minWidth: 200, maxWidth: 240,
          overflow: 'hidden',
          boxShadow: hovered
            ? `0 4px 20px rgba(0,0,0,0.5), 0 0 0 1px ${accentColor}33`
            : '0 2px 8px rgba(0,0,0,0.4)',
          transition: 'box-shadow 0.15s, border-color 0.15s',
          cursor: 'grab', userSelect: 'none', position: 'relative',
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          {comp.avatarUrl && (
            <Box component="img" src={comp.avatarUrl}
              sx={{ width: 28, height: 28, borderRadius: 1, objectFit: 'cover', flexShrink: 0, mt: 0.1 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.82rem', fontWeight: 700, color: 'white', lineHeight: 1.3, mb: 0.5,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {comp.title || '(Namnlös)'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {status && (
              <Box sx={{
                fontSize: '0.6rem', fontWeight: 700, color: status.color,
                bgcolor: status.color + '22', px: 0.6, py: 0.1, borderRadius: 0.5,
              }}>
                {status.label}
              </Box>
            )}
            {cat && (
              <Box sx={{
                fontSize: '0.6rem', color: cat.color, bgcolor: cat.color + '1a',
                px: 0.6, py: 0.1, borderRadius: 0.5,
              }}>
                {cat.name}
              </Box>
            )}
          </Box>
          </Box>
        </Box>

        {hovered && (
          <Box
            onClick={data.onOpen}
            sx={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              bgcolor: accentColor + '22',
              borderTop: `1px solid ${accentColor}44`,
              py: 0.35, textAlign: 'center',
              fontSize: '0.62rem', color: accentColor, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Öppna →
          </Box>
        )}
      </Box>
      <Handle type="source" position={Position.Right} style={HANDLE_STYLE} />
    </>
  );
}

const nodeTypes = { component: ComponentNode, frame: FrameNode };

// ── Page ─────────────────────────────────────────────────────────────────────

export default function MappNMap() {
  const { caseId } = useParams();
  const navigate   = useNavigate();

  const [caseData, setCaseData]         = useState(null);
  const [perspectives, setPerspectives] = useState([]);
  const [categories, setCategories]     = useState([]);
  const [components, setComponents]     = useState([]);
  const [loading, setLoading]           = useState(true);

  const [filterPersp, setFilterPersp]   = useState('');
  const [colorMode, setColorMode]       = useState('status');
  const [filterStatus, setFilterStatus] = useState('');

  const [currentEdgeKind,  setCurrentEdgeKind]  = useState('simple');
  const [currentPathStyle, setCurrentPathStyle] = useState('smoothstep');
  const [showEdgeLabels,   setShowEdgeLabels]   = useState(true);
  const currentPathStyleRef  = useRef('smoothstep');
  const showEdgeLabelsRef    = useRef(true);
  useEffect(() => { currentPathStyleRef.current  = currentPathStyle; }, [currentPathStyle]);
  useEffect(() => { showEdgeLabelsRef.current    = showEdgeLabels;   }, [showEdgeLabels]);

  const [frames, setFrames] = useState([]);

  const [selectedEdge,    setSelectedEdge]    = useState(null);
  const [edgePopoverPos,  setEdgePopoverPos]  = useState(null);
  const selectedEdgeRef = useRef(null);
  useEffect(() => { selectedEdgeRef.current = selectedEdge; }, [selectedEdge]);

  const edgeUpdateSuccessful = useRef(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId]     = useState(null);
  const [detailData, setDetailData] = useState(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges]               = useEdgesState([]);
  const rfInstance       = useRef(null);
  const edgesInitialized = useRef(false);
  const fitViewPending   = useRef(false);
  const initDone         = useRef(false);

  // ── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    let unsub;
    async function init() {
      setLoading(true);
      const [cSnap, pSnap, catSnap] = await Promise.all([
        getDoc(doc(db, 'mappnCases', caseId)),
        getDocs(query(collection(db, 'mappnCases', caseId, 'perspectives'), orderBy('order'))),
        getDocs(query(collection(db, 'mappnCases', caseId, 'categories'),   orderBy('order'))),
      ]);
      if (!cSnap.exists()) { navigate('/applab/mappn'); return; }
      const cd = { id: cSnap.id, ...cSnap.data() };
      setCaseData(cd);
      if (!edgesInitialized.current) {
        const edgeSrc = cd.edges || [];
        setEdges(edgeSrc.map(e => ({
          id: e.id, source: e.source, target: e.target,
          ...buildEdgeProps(e.edgeKind || 'simple', currentPathStyleRef.current, showEdgeLabelsRef.current),
        })));
        edgesInitialized.current = true;
      }
      setFrames(cd.frames || []);
      setPerspectives(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      unsub = onSnapshot(
        query(collection(db, 'mappnCases', caseId, 'components'), orderBy('createdAt')),
        snap => { setComponents(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      );
      initDone.current = true;
    }
    init();
    return () => unsub?.();
  }, [caseId, navigate]); // eslint-disable-line

  // ── Reload edges + frames when perspective changes ──────────────────────────
  useEffect(() => {
    if (!initDone.current || !caseId) return;
    async function reload() {
      const snap = await getDoc(doc(db, 'mappnCases', caseId));
      if (!snap.exists()) return;
      const d = snap.data();
      const edgeSrc  = filterPersp ? (d.perspectiveEdges?.[filterPersp]  || []) : (d.edges  || []);
      const frameSrc = filterPersp ? (d.perspectiveFrames?.[filterPersp] || []) : (d.frames || []);
      setEdges(edgeSrc.map(e => ({
        id: e.id, source: e.source, target: e.target,
        ...buildEdgeProps(e.edgeKind || 'simple', currentPathStyleRef.current, showEdgeLabelsRef.current),
      })));
      setFrames(frameSrc);
    }
    reload();
  }, [filterPersp]); // eslint-disable-line

  // ── Update all edges when path style changes ─────────────────────────────────
  useEffect(() => {
    setEdges(prev => prev.map(e => ({
      ...e,
      ...buildEdgeProps(e.data?.edgeKind || 'simple', currentPathStyle, showEdgeLabelsRef.current),
    })));
  }, [currentPathStyle]); // eslint-disable-line

  // ── Update all edges when label visibility changes ───────────────────────────
  useEffect(() => {
    setEdges(prev => prev.map(e => ({
      ...e,
      ...buildEdgeProps(e.data?.edgeKind || 'simple', currentPathStyleRef.current, showEdgeLabels),
    })));
  }, [showEdgeLabels]); // eslint-disable-line

  // ── Build component nodes ────────────────────────────────────────────────────
  useEffect(() => {
    if (!components.length && !loading) {
      setNodes(prev => prev.filter(n => n.type === 'frame'));
      return;
    }
    if (!components.length) return;
    const posKey = filterPersp || 'default';

    let visible = components;
    if (filterPersp) visible = visible.filter(c => (c.perspectiveIds || []).includes(filterPersp));
    if (filterStatus) visible = visible.filter(c => c.status === filterStatus);
    if (filterPersp) {
      visible = visible.filter(c => {
        const p = c.priorityByPerspective?.[filterPersp];
        return !p || p < 3;
      });
    }

    const compNodes = visible.map((comp, idx) => {
      const savedPos = comp.position?.[posKey];
      const position = savedPos ?? autoPos(idx);
      const priority = filterPersp ? (comp.priorityByPerspective?.[filterPersp] ?? 1) : 1;
      return {
        id: comp.id, type: 'component', position, zIndex: 1,
        data: {
          comp, categories, colorMode, priority,
          onOpen: () => { setDetailId(comp.id); setDetailData(comp); setDetailOpen(true); },
        },
      };
    });

    setNodes(prev => {
      const frameNodes = prev.filter(n => n.type === 'frame');
      return [...frameNodes, ...compNodes];
    });

    if (compNodes.length) {
      if (rfInstance.current) {
        setTimeout(() => rfInstance.current?.fitView({ padding: 0.15, duration: 400 }), 80);
      } else {
        fitViewPending.current = true;
      }
    }
  }, [components, categories, filterPersp, filterStatus, colorMode, loading]); // eslint-disable-line

  // ── Sync frame nodes into ReactFlow nodes ────────────────────────────────────
  useEffect(() => {
    setNodes(prev => {
      const compNodes = prev.filter(n => n.type !== 'frame');
      // Preserve selection state so that resizing/recoloring doesn't drop selection
      const selectedIds = new Set(prev.filter(n => n.selected).map(n => n.id));
      const frameNodes = frames.map(f => ({
        id:       f.id,
        type:     'frame',
        selected: selectedIds.has(f.id),
        position: { x: f.x || 80, y: f.y || 80 },
        style:    { width: f.width || 380, height: f.height || 240 },
        zIndex:   -1,
        data: {
          label: f.label || '',
          color: f.color || '',
          onRename: (newLabel) => {
            setFrames(prev2 => {
              const updated = prev2.map(fr => fr.id === f.id ? { ...fr, label: newLabel } : fr);
              saveFramesFn(updated);
              return updated;
            });
          },
          onResize: (params) => {
            setFrames(prev2 => {
              const updated = prev2.map(fr => fr.id === f.id ? {
                ...fr,
                x: Math.round(params.x), y: Math.round(params.y),
                width: Math.round(params.width), height: Math.round(params.height),
              } : fr);
              saveFramesFn(updated);
              return updated;
            });
          },
          onColorChange: (newColor) => {
            setFrames(prev2 => {
              const updated = prev2.map(fr => fr.id === f.id ? { ...fr, color: newColor } : fr);
              saveFramesFn(updated);
              return updated;
            });
          },
          onDelete: () => {
            setFrames(prev2 => {
              const updated = prev2.filter(fr => fr.id !== f.id);
              saveFramesFn(updated);
              return updated;
            });
            setNodes(prev2 => prev2.filter(n => n.id !== f.id));
          },
        },
      }));
      return [...frameNodes, ...compNodes];
    });
  }, [frames]); // eslint-disable-line

  // ── Save helpers ─────────────────────────────────────────────────────────────

  const saveEdges = useCallback(async (edgesArr) => {
    const field = filterPersp
      ? `perspectiveEdges.${filterPersp}`
      : 'edges';
    try {
      await updateDoc(doc(db, 'mappnCases', caseId), {
        [field]: edgesArr.map(e => ({
          id: e.id, source: e.source, target: e.target,
          edgeKind: e.data?.edgeKind || 'simple',
        })),
      });
    } catch { /* non-critical */ }
  }, [caseId, filterPersp]);

  function saveFramesFn(framesArr) {
    const field = filterPersp ? `perspectiveFrames.${filterPersp}` : 'frames';
    updateDoc(doc(db, 'mappnCases', caseId), { [field]: framesArr }).catch(() => {});
  }

  function addFrame() {
    const id  = uid();
    const vp  = rfInstance.current?.getViewport() || { x: 0, y: 0, zoom: 1 };
    const cx  = Math.round((-vp.x + 200) / vp.zoom);
    const cy  = Math.round((-vp.y + 100) / vp.zoom);
    const nf  = { id, label: '', x: cx, y: cy, width: 380, height: 240 };
    setFrames(prev => {
      const updated = [...prev, nf];
      saveFramesFn(updated);
      return updated;
    });
  }

  // ── Node drag / edge events ─────────────────────────────────────────────────

  const onNodeDragStop = useCallback(async (_, node) => {
    if (node.type === 'frame') {
      setFrames(prev => {
        const updated = prev.map(f =>
          f.id === node.id ? { ...f, x: Math.round(node.position.x), y: Math.round(node.position.y) } : f
        );
        saveFramesFn(updated);
        return updated;
      });
      return;
    }
    const posKey = filterPersp || 'default';
    try {
      await updateDoc(doc(db, 'mappnCases', caseId, 'components', node.id), {
        [`position.${posKey}`]: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
      });
    } catch { /* non-critical */ }
  }, [caseId, filterPersp]); // eslint-disable-line

  const handleNodesChange = useCallback((changes) => {
    const removals = changes.filter(c => c.type === 'remove');
    if (removals.length) {
      const ids = new Set(removals.map(c => c.id));
      setFrames(prev => {
        const hasFrame = prev.some(f => ids.has(f.id));
        if (!hasFrame) return prev;
        const updated = prev.filter(f => !ids.has(f.id));
        saveFramesFn(updated);
        return updated;
      });
    }
    onNodesChange(changes);
  }, [onNodesChange]); // eslint-disable-line

  const onConnect = useCallback((params) => {
    const newEdge = {
      ...params, id: uid(),
      ...buildEdgeProps(currentEdgeKind, currentPathStyle, showEdgeLabelsRef.current),
    };
    setEdges(eds => {
      const next = addEdge(newEdge, eds);
      saveEdges(next);
      return next;
    });
  }, [saveEdges, currentEdgeKind, currentPathStyle]); // eslint-disable-line

  const onEdgesChange = useCallback((changes) => {
    setEdges(eds => {
      const next = applyEdgeChanges(changes, eds);
      if (changes.some(c => c.type === 'remove')) {
        saveEdges(next);
        const removedIds = new Set(changes.filter(c => c.type === 'remove').map(c => c.id));
        if (removedIds.has(selectedEdgeRef.current?.id)) {
          setSelectedEdge(null);
          setEdgePopoverPos(null);
        }
      }
      return next;
    });
  }, [saveEdges, setEdges]);

  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    if (selectedEdgeRef.current?.id === edge.id) {
      setSelectedEdge(null);
      setEdgePopoverPos(null);
    } else {
      setSelectedEdge(edge);
      setEdgePopoverPos({ x: event.clientX, y: event.clientY });
    }
  }, []);

  const onEdgeUpdate = useCallback((oldEdge, newConnection) => {
    edgeUpdateSuccessful.current = true;
    setEdges(els => {
      const updated = rfUpdateEdge(oldEdge, newConnection, els);
      saveEdges(updated);
      return updated;
    });
    setSelectedEdge(null);
    setEdgePopoverPos(null);
  }, [saveEdges, setEdges]); // eslint-disable-line

  const onEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false;
  }, []);

  const onEdgeUpdateEnd = useCallback((_, edge) => {
    if (!edgeUpdateSuccessful.current) {
      setEdges(eds => {
        const next = eds.filter(e => e.id !== edge.id);
        saveEdges(next);
        return next;
      });
    }
    edgeUpdateSuccessful.current = true;
  }, [saveEdges, setEdges]); // eslint-disable-line

  function handleSaved(savedId, savedData) {
    setComponents(prev => prev.map(c => c.id === savedId ? { ...c, ...savedData } : c));
  }
  function handleDeleted(deletedId) {
    setComponents(prev => prev.filter(c => c.id !== deletedId));
    setDetailOpen(false);
  }

  function closePopover() {
    setSelectedEdge(null);
    setEdgePopoverPos(null);
  }

  const compCount = nodes.filter(n => n.type === 'component').length;
  const hasHero = !!(caseData?.heroVideoUrl || caseData?.heroImageUrl);
  const curPerspLabel = filterPersp
    ? (perspectives.find(p => p.id === filterPersp)?.name || '')
    : '';

  const TABS = [
    { label: 'Kort',          path: 'cards',        icon: <ViewModuleIcon sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Perspektiv',    path: 'perspectives', icon: <ViewColumnIcon sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Karta',         path: 'map',          icon: <MapIcon        sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Tabell',        path: 'table',        icon: <TableRowsIcon  sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Tech',          path: 'tech',         icon: <HubIcon        sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Inställningar', path: 'settings',     icon: <SettingsIcon   sx={{ fontSize: '0.9rem' }} /> },
  ];

  const SEL_SX = {
    color: 'white', fontSize: '0.78rem',
    bgcolor: 'rgba(255,255,255,0.04)',
    '& .MuiOutlinedInput-notchedOutline':       { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: MAPPN_ACCENT },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
  };

  const pillSx = (active, color) => ({
    px: 1.2, py: 0.45, borderRadius: 1, cursor: 'pointer', fontSize: '0.7rem', fontWeight: active ? 700 : 400,
    bgcolor: active ? color + '28' : 'rgba(255,255,255,0.05)',
    color:   active ? color        : 'rgba(255,255,255,0.4)',
    border: `1px solid ${active ? color + '55' : 'transparent'}`,
    transition: 'all 0.12s', userSelect: 'none',
    '&:hover': { bgcolor: color + '18' },
  });

  const glassPanel = {
    bgcolor: hasHero ? 'rgba(13,17,23,0.72)' : 'rgba(13,17,23,0.92)',
    backdropFilter: hasHero ? 'blur(20px) saturate(1.3)' : 'none',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 2, px: 1.5, py: 1,
    boxShadow: hasHero ? '0 4px 24px rgba(0,0,0,0.4)' : 'none',
  };

  return (
    <>
    <Toaster richColors position="top-center" />
    <Box sx={{
      height: '100vh', bgcolor: '#0d1117', color: 'white',
      display: 'grid', gridTemplateRows: 'auto 1fr',
      position: 'relative', overflow: 'hidden',
    }}>

      {/* Hero media background */}
      {hasHero && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
          {caseData.heroVideoUrl ? (
            <Box component="video" src={caseData.heroVideoUrl}
              autoPlay muted loop playsInline
              sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.42 }} />
          ) : (
            <Box component="img" src={caseData.heroImageUrl}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.32 }} />
          )}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(160deg, rgba(13,17,23,0.18) 0%, rgba(13,17,23,0.12) 100%)',
          }} />
        </Box>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        position: 'relative', zIndex: 2,
        bgcolor:        hasHero ? 'rgba(13,17,23,0.72)' : '#161b22',
        backdropFilter: hasHero ? 'blur(22px) saturate(1.3)' : 'none',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/applab/mappn')}>
          <ArrowBackIcon />
        </IconButton>
        {caseData?.heroImageUrl && (
          <Box component="img" src={caseData.heroImageUrl}
            sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {caseData?.name || '…'}
            {curPerspLabel && (
              <Box component="span" sx={{ ml: 1, fontSize: '0.75rem', fontWeight: 400, color: 'rgba(255,255,255,0.4)' }}>
                / {curPerspLabel}
              </Box>
            )}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
            {compCount} av {components.length} komponenter · {edges.length} relationer · {frames.length} grupper
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {TABS.map(tab => (
            <Button key={tab.path} startIcon={tab.icon}
              onClick={() => navigate(`/applab/mappn/${caseId}/${tab.path}`)}
              sx={{
                textTransform: 'none', fontSize: '0.8rem',
                color:  tab.path === 'map' ? MAPPN_ACCENT : 'rgba(255,255,255,0.5)',
                bgcolor: tab.path === 'map' ? MAPPN_ACCENT + '18' : 'transparent',
                borderRadius: 1, '&:hover': { bgcolor: MAPPN_ACCENT + '22' },
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* ── Canvas ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress sx={{ color: MAPPN_ACCENT }} />
        </Box>
      ) : (
        <Box sx={{ position: 'relative', zIndex: 2, overflow: 'hidden' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeDragStop={onNodeDragStop}
            onNodeClick={(_, node) => {
              closePopover();
              if (node.type !== 'component') return;
              const comp = components.find(c => c.id === node.id);
              if (comp) { setDetailId(node.id); setDetailData(comp); setDetailOpen(true); }
            }}
            onEdgeClick={onEdgeClick}
            onEdgeUpdate={onEdgeUpdate}
            onEdgeUpdateStart={onEdgeUpdateStart}
            onEdgeUpdateEnd={onEdgeUpdateEnd}
            edgeUpdaterRadius={12}
            onPaneClick={closePopover}
            onInit={instance => {
              rfInstance.current = instance;
              if (fitViewPending.current) {
                fitViewPending.current = false;
                setTimeout(() => instance.fitView({ padding: 0.15, duration: 400 }), 80);
              }
            }}
            connectionLineStyle={{
              stroke: (EDGE_KINDS.find(e => e.key === currentEdgeKind) || EDGE_KINDS[0]).color + 'cc',
              strokeWidth: 2,
            }}
            deleteKeyCode="Delete"
            minZoom={0.1}
            maxZoom={2.5}
            proOptions={{ hideAttribution: true }}
            style={{ background: hasHero ? 'rgba(13,17,23,0.3)' : '#0d1117' }}
          >
            <Background color={hasHero ? 'rgba(255,255,255,0.06)' : '#1e2530'} gap={28} size={1} />
            <Controls style={{
              background: hasHero ? 'rgba(13,17,23,0.72)' : '#161b22',
              backdropFilter: hasHero ? 'blur(20px)' : 'none',
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
            }} />
            <MiniMap
              nodeColor={n => {
                if (n.type === 'frame') return 'rgba(255,255,255,0.06)';
                const comp = components.find(c => c.id === n.id);
                return STATUS_OPTIONS.find(s => s.key === comp?.status)?.color || '#555';
              }}
              style={{
                background: hasHero ? 'rgba(13,17,23,0.72)' : '#161b22',
                backdropFilter: hasHero ? 'blur(20px)' : 'none',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
              }}
              maskColor="rgba(0,0,0,0.6)"
            />

            {/* ── Single-row toolbar ── */}
            <Panel position="top-left">
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                overflowX: 'auto', maxWidth: 'calc(100vw - 140px)',
                '&::-webkit-scrollbar': { height: 3 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
                ...glassPanel,
                py: 0.75,
              }}>
                {/* Perspective */}
                <FormControl size="small" sx={{ minWidth: 125, flexShrink: 0 }}>
                  <Select value={filterPersp} onChange={e => setFilterPersp(e.target.value)} displayEmpty sx={SEL_SX}>
                    <MenuItem value=""><em style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}>Alla perspektiv</em></MenuItem>
                    {perspectives.map(p => (
                      <MenuItem key={p.id} value={p.id} sx={{ fontSize: '0.82rem' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color, mr: 1, display: 'inline-block' }} />
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Status filter */}
                <FormControl size="small" sx={{ minWidth: 110, flexShrink: 0 }}>
                  <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} displayEmpty sx={SEL_SX}>
                    <MenuItem value=""><em style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}>Alla statusar</em></MenuItem>
                    {STATUS_OPTIONS.map(s => (
                      <MenuItem key={s.key} value={s.key} sx={{ fontSize: '0.82rem' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, mr: 1, display: 'inline-block' }} />
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Color mode */}
                <Box sx={{ display: 'flex', gap: 0.4, flexShrink: 0 }}>
                  {[
                    { key: 'status',         label: 'Status'   },
                    { key: 'category',       label: 'Kat.'     },
                    { key: 'implementation', label: 'Impl.'    },
                  ].map(m => (
                    <Box key={m.key} onClick={() => setColorMode(m.key)} sx={pillSx(colorMode === m.key, MAPPN_ACCENT)}>
                      {m.label}
                    </Box>
                  ))}
                </Box>

                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Add group */}
                <Tooltip title="Lägg till grupp (ram)">
                  <Box onClick={addFrame} sx={{
                    ...pillSx(false, 'rgba(255,255,255,0.6)'),
                    color: 'rgba(255,255,255,0.5)', borderStyle: 'dashed', flexShrink: 0,
                  }}>
                    + Grupp
                  </Box>
                </Tooltip>

                {/* Labels toggle */}
                <Tooltip title="Visa/dölj piletiketter">
                  <Box onClick={() => setShowEdgeLabels(v => !v)}
                    sx={{ ...pillSx(showEdgeLabels, MAPPN_ACCENT), flexShrink: 0 }}>
                    Labels
                  </Box>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Edge kinds */}
                <Box sx={{ display: 'flex', gap: 0.4, flexShrink: 0 }}>
                  {EDGE_KINDS.map(ek => (
                    <Box key={ek.key} onClick={() => setCurrentEdgeKind(ek.key)}
                      sx={pillSx(currentEdgeKind === ek.key, ek.color)}>
                      {ek.label}
                    </Box>
                  ))}
                </Box>

                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Path styles */}
                <Box sx={{ display: 'flex', gap: 0.4, flexShrink: 0 }}>
                  {PATH_STYLES.map(ps => (
                    <Box key={ps.key} onClick={() => setCurrentPathStyle(ps.key)}
                      sx={pillSx(currentPathStyle === ps.key, MAPPN_ACCENT)}>
                      {ps.label}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Panel>
          </ReactFlow>

          {/* ── Edge edit popover ── */}
          {selectedEdge && edgePopoverPos && (
            <Box
              onClick={e => e.stopPropagation()}
              sx={{
                position: 'fixed',
                left: edgePopoverPos.x + 12,
                top: edgePopoverPos.y - 8,
                zIndex: 9999,
                ...glassPanel,
                p: 1.5,
                minWidth: 200,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', mb: 0.25 }}>
                Kopplingtyp
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
                {EDGE_KINDS.map(ek => (
                  <Box
                    key={ek.key}
                    onClick={() => {
                      const newProps = buildEdgeProps(ek.key, currentPathStyleRef.current, showEdgeLabelsRef.current);
                      const updated = { ...selectedEdge, ...newProps };
                      setEdges(prev => {
                        const next = prev.map(e => e.id === selectedEdge.id ? updated : e);
                        saveEdges(next);
                        return next;
                      });
                      setSelectedEdge(updated);
                    }}
                    sx={pillSx(selectedEdge.data?.edgeKind === ek.key, ek.color)}
                  >
                    {ek.label}
                  </Box>
                ))}
              </Box>
              <Box
                onClick={() => {
                  setEdges(prev => {
                    const next = prev.filter(e => e.id !== selectedEdge.id);
                    saveEdges(next);
                    return next;
                  });
                  closePopover();
                }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  mt: 0.25, px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer',
                  color: '#f87171', fontSize: '0.72rem',
                  '&:hover': { bgcolor: 'rgba(248,113,113,0.1)' },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                Radera koppling
              </Box>
            </Box>
          )}
        </Box>
      )}

      <ComponentDetail
        open={detailOpen}
        caseId={caseId}
        componentId={detailId}
        initialData={detailData}
        perspectives={perspectives}
        categories={categories}
        caseTechStack={caseData?.techStack || []}
        onClose={() => setDetailOpen(false)}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </Box>
    </>
  );
}
